import React, { useMemo, useState } from 'react';
import { Empty, Space, Tag } from 'antd';
import { useTranslate } from '@/translations';
import { AppFilter, AppSearch } from '@/types';
import {
  CustomColumns,
  CustomTable,
  faAngleRight,
  Icon,
} from '@/ui/components';
import { FORMAT_FULL_TIME_DAYJS, StorageKeys } from '@/utils/constants';
import { timeConverter } from '@/utils/helpers';
import {
  QuestionnaireResponseSummary,
  QuestionnaireResponsesQuery,
  useQuestionnaireResponses,
} from '@/api/Questionnaire';
import { useResponseLabels } from '../../useResponseLabels';
import { ResponseIdLink } from './ResponsesDataGrid.styled';

const defaultPageSize = 10;
const operatorTokens = new Set(['Equal', 'Not', 'Contains']);

const filterValues = (
  filters: AppFilter<QuestionnaireResponseSummary>[] | undefined,
  key: keyof QuestionnaireResponseSummary,
) =>
  filters
    ?.find((f) => f.key === key)
    ?.value?.filter((v) => !operatorTokens.has(v));

const mapAppSearchToQuery = (
  search: AppSearch<QuestionnaireResponseSummary>,
): QuestionnaireResponsesQuery => {
  const customerIds = filterValues(search.filters, 'customerId');
  const isSubmittedAtSort = search.sortField === 'submittedAt';

  return {
    pageNumber: search.page ?? 1,
    pageSize: search.pageSize ?? defaultPageSize,
    sortField: isSubmittedAtSort ? 'submittedAt' : undefined,
    sortOrder: isSubmittedAtSort ? search.sortOrder : undefined,
    vin: filterValues(search.filters, 'vin')?.[0] as string | undefined,
    templateKey: filterValues(search.filters, 'templateKey')?.[0] as
      | string
      | undefined,
    customerIds:
      customerIds && customerIds.length > 0
        ? (customerIds as number[])
        : undefined,
  };
};

const defaultAppSearch: AppSearch<QuestionnaireResponseSummary> = {
  page: 1,
  pageSize: defaultPageSize,
  filters: [],
  sortField: 'submittedAt',
  sortOrder: 'descend',
};

interface ResponsesDataGridProps {
  responsePath: (id: number) => string;
}

export const ResponsesDataGrid = ({ responsePath }: ResponsesDataGridProps) => {
  const { t } = useTranslate();

  const [appSearch, setAppSearch] =
    useState<AppSearch<QuestionnaireResponseSummary>>();

  const query = useMemo(
    () => (appSearch ? mapAppSearchToQuery(appSearch) : undefined),
    [appSearch],
  );

  const { data, isFetching } = useQuestionnaireResponses(query);
  const {
    customers,
    templates,
    templateTitleByKey,
    customerNameById,
    isLoading: labelsLoading,
  } = useResponseLabels();

  const responses = data?.responses ?? [];
  const totalCount = data?.totalCount ?? 0;

  const templateFilterOptions = useMemo(() => {
    const seen = new Set<string>();
    return templates.reduce<{ text: string; value: string }[]>(
      (acc, template) => {
        if (!seen.has(template.templateKey)) {
          seen.add(template.templateKey);
          acc.push({
            text: template.title || template.templateKey,
            value: template.templateKey,
          });
        }
        return acc;
      },
      [],
    );
  }, [templates]);

  const customerFilterOptions = useMemo(
    () => customers.map((c) => ({ text: c.customerName, value: c.customerId })),
    [customers],
  );

  const columns: CustomColumns<QuestionnaireResponseSummary> = useMemo(
    () => [
      {
        dataIndex: 'id',
        title: t('responseId'),
        width: 140,
        fixed: 'left',
        render: (value: number) => (
          <ResponseIdLink to={responsePath(value)}>
            R-{value} <Icon icon={faAngleRight} />
          </ResponseIdLink>
        ),
      },
      {
        dataIndex: 'templateKey',
        title: t('template'),
        renderType: 'select',
        filter: true,
        filterOptions: templateFilterOptions,
        render: (value: string, record) => (
          <Space>
            <span>{templateTitleByKey[value] ?? value}</span>
            <Tag>v{record.templateVersion}</Tag>
          </Space>
        ),
      },
      {
        dataIndex: 'vin',
        title: t('vin'),
        renderType: 'input',
        filter: true,
      },
      {
        dataIndex: 'customerId',
        title: t('customer'),
        renderType: 'select',
        filter: true,
        filterOptions: customerFilterOptions,
        render: (value?: number) =>
          value != null ? (customerNameById[value] ?? `#${value}`) : '-',
      },
      {
        dataIndex: 'submittedAt',
        title: t('submittedAt'),
        sortable: true,
        render: (value: string) => timeConverter(value, FORMAT_FULL_TIME_DAYJS),
      },
      {
        dataIndex: 'submittedBy',
        title: t('submittedBy'),
      },
      {
        dataIndex: 'stockId',
        title: t('stockId'),
        hidden: true,
      },
      {
        dataIndex: 'subCustomerId',
        title: t('subCustomer'),
        hidden: true,
      },
    ],
    [
      customerFilterOptions,
      customerNameById,
      responsePath,
      t,
      templateFilterOptions,
      templateTitleByKey,
    ],
  );

  return (
    <CustomTable
      dataSource={responses}
      columns={columns}
      rowKey="id"
      loading={isFetching || labelsLoading}
      pagination={{
        total: totalCount,
        pageSize: appSearch?.pageSize,
        current: appSearch?.page || 1,
      }}
      appSearchOptions={{
        storageKey: StorageKeys.SearchQuestionnaireResponses,
        defaultSearch: defaultAppSearch,
        value: appSearch,
        onChange: setAppSearch,
      }}
      locale={{
        emptyText: <Empty description={t('noQuestionnaireResponses')} />,
      }}
    />
  );
};
